// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Runtime configuration: `/config.json`, read once before the first render.
 *
 * The Docker image writes it from the container's environment
 * (`docker-entrypoint.sh`), so one build serves every deployment. With no
 * file — `ruxd --local` serves none, and its SPA fallback may answer with
 * `index.html` instead — or with no `supabaseUrl` in it, the app keeps the
 * local behaviour: no login, the server's implicit user.
 *
 * Kept free of React and the DOM so it is unit-tested in Node.
 */

export interface SupabaseConfig {
  /** The Supabase API origin, e.g. `https://rux.example.dk` (same origin, `/auth/v1` → kong). */
  url: string;
  /** The anon key, sent as `apikey`. Public by design. */
  anonKey: string;
  /** Offer "send a login link" (needs SMTP on the Supabase side). */
  magicLink: boolean;
}

export interface RuntimeConfig {
  /** Null: no Supabase, no login (local mode). */
  supabase: SupabaseConfig | null;
}

export const LOCAL_CONFIG: RuntimeConfig = Object.freeze({ supabase: null });

export const CONFIG_PATH = '/config.json';

/**
 * Turns the parsed file into a config. Anything that is not an object with
 * a non-empty `supabaseUrl` and `supabaseAnonKey` is local mode; a URL that
 * is not http(s) is refused the same way, rather than handed to the client.
 */
export function parseConfig(raw: unknown): RuntimeConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return LOCAL_CONFIG;
  const o = raw as Record<string, unknown>;
  const url = typeof o.supabaseUrl === 'string' ? o.supabaseUrl.trim().replace(/\/+$/, '') : '';
  const anonKey = typeof o.supabaseAnonKey === 'string' ? o.supabaseAnonKey.trim() : '';
  if (!url || !anonKey) return LOCAL_CONFIG;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return LOCAL_CONFIG;
  } catch {
    return LOCAL_CONFIG;
  }
  return { supabase: { url, anonKey, magicLink: o.magicLink === true } };
}

/**
 * Fetches and parses `/config.json`. Never rejects: a missing file, an HTML
 * fallback, bad JSON or a network failure all mean local mode — the server
 * then decides whether anyone needs to sign in.
 */
export async function loadConfig(
  doFetch: (input: string, init?: RequestInit) => Promise<Response> = (i, init) => fetch(i, init),
): Promise<RuntimeConfig> {
  try {
    const response = await doFetch(CONFIG_PATH, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) return LOCAL_CONFIG;
    const type = response.headers.get('Content-Type') ?? '';
    if (type && !/json/i.test(type)) return LOCAL_CONFIG;
    return parseConfig(JSON.parse(await response.text()));
  } catch {
    return LOCAL_CONFIG;
  }
}

let current: RuntimeConfig = LOCAL_CONFIG;

/** The config `main.tsx` loaded before rendering (local until then). */
export function runtimeConfig(): RuntimeConfig {
  return current;
}

export function setRuntimeConfig(config: RuntimeConfig): void {
  current = config;
}
